import { MDXProvider as BaseMDXProvider } from '@mdx-js/react';
import {
  Typography,
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import type { PageColorConfig } from '@/theme/colors';
import { usePageColorsSafe } from '@/contexts/PageColorContext';

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function buildComponents(colors?: PageColorConfig, isDark?: boolean): Record<string, any> {
  const accent = colors
    ? isDark ? colors.accent : colors.primary
    : undefined;

  return {
    h1: (props: any) => (
      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        fontWeight="bold"
        sx={{
          fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
          lineHeight: 1.2,
        }}
        {...props}
      />
    ),
    h2: (props: any) => (
      <Typography
        variant="h3"
        component="h2"
        gutterBottom
        fontWeight="bold"
        sx={{
          mt: 4,
          mb: 2,
          fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
          lineHeight: 1.3,
        }}
        {...props}
      />
    ),
    h3: (props: any) => (
      <Typography
        variant="h4"
        component="h3"
        gutterBottom
        fontWeight="600"
        sx={{
          mt: 3,
          mb: 1.5,
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '1.875rem' },
          lineHeight: 1.3,
        }}
        {...props}
      />
    ),
    h4: (props: any) => (
      <Typography
        variant="h5"
        component="h4"
        gutterBottom
        fontWeight="600"
        sx={{
          mt: 2,
          mb: 1,
          fontSize: { xs: '1.25rem', sm: '1.375rem', md: '1.5rem' },
          lineHeight: 1.4,
        }}
        {...props}
      />
    ),
    h5: (props: any) => (
      <Typography
        variant="h6"
        component="h5"
        gutterBottom
        fontWeight="600"
        sx={{
          mt: 2,
          mb: 1,
          fontSize: { xs: '1.125rem', sm: '1.25rem' },
          lineHeight: 1.5,
        }}
        {...props}
      />
    ),
    h6: (props: any) => (
      <Typography
        variant="subtitle1"
        component="h6"
        gutterBottom
        fontWeight="600"
        sx={{
          mt: 1.5,
          mb: 0.5,
          fontSize: '1.125rem',
          lineHeight: 1.5,
        }}
        {...props}
      />
    ),

    p: (props: any) => (
      <Typography variant="body1" paragraph sx={{ lineHeight: 1.7 }} {...props} />
    ),

    a: (props: any) => (
      <Link
        {...props}
        target={props.href?.startsWith('http') ? '_blank' : undefined}
        rel={props.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        sx={{
          color: accent ?? 'primary.main',
          textDecoration: 'none',
          fontWeight: 500,
          borderBottom: '1px solid',
          borderColor: accent ? hexToRgba(accent, 0.3) : 'primary.light',
          transition: 'border-color 0.2s, color 0.2s',
          '&:hover': {
            borderColor: accent ?? 'primary.main',
            textDecoration: 'none',
          },
        }}
      />
    ),

    ul: (props: any) => (
      <Box
        component="ul"
        {...props}
        sx={{
          pl: 3,
          mb: 2.5,
          listStyleType: 'disc',
          '& ul': { listStyleType: 'circle', mt: 0.5 },
          '& ul ul': { listStyleType: 'square' },
        }}
      />
    ),
    ol: (props: any) => (
      <Box
        component="ol"
        {...props}
        sx={{
          pl: 3,
          mb: 2.5,
          listStyleType: 'decimal',
          '& ol': { listStyleType: 'lower-alpha', mt: 0.5 },
          '& ol ol': { listStyleType: 'lower-roman' },
        }}
      />
    ),
    li: (props: any) => (
      <Typography
        component="li"
        variant="body1"
        {...props}
        sx={{
          mb: 0.75,
          lineHeight: 1.7,
          pl: 0.5,
          '&::marker': accent ? { color: accent } : undefined,
        }}
      />
    ),

    code: (props: any) => {
      const { className, children, ...rest } = props;

      if (!className) {
        return (
          <Box
            component="code"
            sx={{
              px: 1,
              py: 0.4,
              borderRadius: 1,
              bgcolor: accent ? hexToRgba(accent, 0.08) : 'rgba(175, 184, 193, 0.2)',
              color: accent ?? '#d32f2f',
              fontFamily: 'monospace',
              fontSize: '0.9em',
              fontWeight: 500,
              border: '1px solid',
              borderColor: accent ? hexToRgba(accent, 0.2) : 'rgba(175, 184, 193, 0.3)',
            }}
            {...rest}
          >
            {children}
          </Box>
        );
      }

      return <code className={className} {...rest}>{children}</code>;
    },

    pre: (props: any) => (
      <Box
        component="pre"
        sx={{
          p: 0,
          my: 3,
          overflow: 'auto',
          borderRadius: 2,
          boxShadow: 2,
          '& code': {
            fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
            fontSize: '0.9rem',
            lineHeight: 1.7,
          },
        }}
        {...props}
      />
    ),

    blockquote: (props: any) => (
      <Box
        component="blockquote"
        {...props}
        sx={{
          my: 3,
          mx: 0,
          pl: 3,
          pr: 2,
          py: 2,
          borderLeft: '4px solid',
          borderColor: accent ?? 'primary.main',
          bgcolor: accent ? hexToRgba(accent, 0.05) : 'action.hover',
          borderRadius: 1,
          fontStyle: 'italic',
          color: 'text.secondary',
          '& p': { margin: 0 },
        }}
      />
    ),

    hr: () => <Divider sx={{ my: 3 }} />,

    table: (props: any) => (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          my: 4,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          width: '100%',
          ...(accent && {
            borderRadius: 2,
            border: `1px solid ${hexToRgba(accent, 0.25)}`,
          }),
        }}
      >
        <Table {...props} sx={{ width: '100%' }} />
      </TableContainer>
    ),
    thead: (props: any) => (
      <TableHead
        {...props}
        sx={accent ? {
          background: hexToRgba(accent, 0.12),
          '& .MuiTableCell-root': {
            fontWeight: 700,
            fontSize: '0.85rem',
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            color: accent,
            borderBottom: `2px solid ${hexToRgba(accent, 0.3)}`,
          },
        } : undefined}
      />
    ),
    tbody: (props: any) => (
      <TableBody
        {...props}
        sx={accent ? {
          '& .MuiTableRow-root': {
            '&:nth-of-type(even)': {
              backgroundColor: hexToRgba(accent, 0.04),
            },
            '&:hover': {
              backgroundColor: hexToRgba(accent, 0.08),
            },
            '&:last-child .MuiTableCell-root': { borderBottom: 0 },
          },
          '& .MuiTableCell-root': {
            borderBottomColor: hexToRgba(accent, 0.12),
          },
        } : undefined}
      />
    ),
    tr: (props: any) => <TableRow {...props} />,
    th: (props: any) => {
      const align = props.align || props.style?.textAlign;
      return <TableCell {...props} align={align} />;
    },
    td: (props: any) => {
      const align = props.align || props.style?.textAlign;
      return <TableCell {...props} align={align} sx={{ py: 1.5 }} />;
    },

    img: (props: any) => (
      <Box
        component="img"
        {...props}
        loading="lazy"
        sx={{
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 2,
          my: 3,
          display: 'block',
          boxShadow: 2,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'scale(1.01)',
            boxShadow: 3,
          },
        }}
      />
    ),
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

interface MDXProviderProps {
  children: React.ReactNode;
  pageColors?: PageColorConfig;
}

export function MDXProvider({ children, pageColors }: MDXProviderProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const ctx = usePageColorsSafe();
  const effectiveColors = pageColors ?? ctx?.colors;
  const components = useMemo(
    () => buildComponents(effectiveColors, isDark),
    [effectiveColors, isDark],
  );

  return (
    <BaseMDXProvider components={components}>
      {children}
    </BaseMDXProvider>
  );
}

export const mdxComponents = buildComponents();
